import { useState, useEffect, useMemo } from "react";
import {
  Card,
  Button,
  Space,
  Select,
  Input,
  Row,
  Col,
  Modal,
  message,
  Tag,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import useCustomFees from "../../../hooks/finance/useCustomFees";
import { useFeeTypes } from "../../../hooks/finance/useFeeTypes";
import CustomFeeModal from "../../../components/finance/custom-fees/CustomFeeModal";
import CustomFeeRequestsTable from "../../../components/finance/custom-fees/CustomFeeRequestsTable";
import CustomFeeDetailDrawer from "../../../components/finance/custom-fees/CustomFeeDetailDrawer";
import EditCustomFeeModal from "../../../components/finance/custom-fees/EditCustomFeeModal";

const { Option } = Select;

/**
 * Custom Fees Management Page
 */
const CustomFeesManagement = () => {
  const {
    customFees,
    loading,
    fetchCustomFees,
    bulkCreateCustomFees,
    approveCustomFee,
    rejectCustomFee,
    deleteCustomFee,
    updateCustomFee,
  } = useCustomFees();

  const { feeTypes, fetchFeeTypes } = useFeeTypes();

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectingGroupKey, setRejectingGroupKey] = useState(null);
  const [rejectingStudentSpc, setRejectingStudentSpc] = useState(null);

  const [filters, setFilters] = useState({
    searchText: "",
    status: null,
  });
  const [rejectingStudentRequestId, setRejectingStudentRequestId] =
    useState(null);

  useEffect(() => {
    fetchCustomFees();
    fetchFeeTypes({ page: 1, pageSize: 100 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedRequests = useMemo(() => {
    const grouped = new Map();

    customFees.forEach((fee) => {
      const groupKey = `${fee.description}|${fee.feeTypeName}|${fee.amount}|${fee.acadyr}`;

      if (!grouped.has(groupKey)) {
        const feeType = feeTypes.find((ft) => ft.code === fee.feeTypeCode);
        const feeTypeId = feeType?.feeTypeId || feeType?.id;

        grouped.set(groupKey, {
          groupKey,
          customFeeRequestIds: [],
          feeTypeName: fee.feeTypeName,
          feeTypeCode: fee.feeTypeCode,
          feeTypeId: feeTypeId,
          description: fee.description,
          amount: fee.amount,
          acadyr: fee.acadyr,
          status: fee.status,
          requestedById: fee.requestedById,
          requestedByName: fee.requestedByName,
          approvedById: fee.approvedById,
          approvedByName: fee.approvedByName,
          approvedAt: fee.approvedAt,
          rejectionReason: fee.rejectionReason,
          createdAt: fee.createdAt,
          updatedAt: fee.updatedAt,
          students: [],
        });
      }

      const group = grouped.get(groupKey);
      if (!group.customFeeRequestIds.includes(fee.customFeeRequestId)) {
        group.customFeeRequestIds.push(fee.customFeeRequestId);
      }

      group.students.push({
        studentSpc: fee.studentSpc,
        studentName: fee.studentName,
        studentFeeAssignmentId: fee.studentFeeAssignmentId,
        customFeeRequestId: fee.customFeeRequestId,
        status: fee.status,
        rejectionReason: fee.rejectionReason || null,
      });
    });

    grouped.forEach((group) => {
      const allApproved = group.students.every((s) => s.status === "Approved");
      const anyRejected = group.students.some((s) => s.status === "Rejected");

      if (allApproved) {
        group.status = "Approved";
      } else if (
        anyRejected &&
        group.students.some((s) => s.status === "Pending")
      ) {
        group.status = "Mixed";
      } else if (anyRejected) {
        group.status = "Rejected";
      } else {
        group.status = "Pending";
      }
    });

    return Array.from(grouped.values());
  }, [customFees, feeTypes]);

  const filteredRequests = useMemo(() => {
    return groupedRequests.filter((group) => {
      const matchesSearch =
        !filters.searchText ||
        group.description
          .toLowerCase()
          .includes(filters.searchText.toLowerCase()) ||
        group.feeTypeName
          .toLowerCase()
          .includes(filters.searchText.toLowerCase());

      const matchesStatus = !filters.status || group.status === filters.status;

      return matchesSearch && matchesStatus;
    });
  }, [groupedRequests, filters]);

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    fetchCustomFees();
  };

  const handleViewDetails = (group) => {
    setSelectedGroup(group);
    setDetailDrawerVisible(true);
  };

  const handleEdit = (group) => {
    setSelectedGroup(group);
    setEditModalVisible(true);
  };

  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setSelectedGroup(null);
    fetchCustomFees();
  };

  /**
   * Approve entire group - sequential
   */
  const handleApprove = async (groupKey) => {
    const group = groupedRequests.find((g) => g.groupKey === groupKey);

    Modal.confirm({
      title: "Approve Custom Fee Request Group?",
      content: `This will approve the request for all ${group.students.length} student(s).`,
      okText: "Approve",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setApprovalLoading(true);
          for (const requestId of group.customFeeRequestIds) {
            const result = await approveCustomFee(requestId);
            if (result !== true) {
              throw new Error(`Failed to approve request ${requestId}`);
            }
          }
          message.success(`Approved for ${group.students.length} student(s)`);
          setDetailDrawerVisible(false);
          await fetchCustomFees();
        } catch (error) {
          console.error("Approve error:", error);
          message.error("Failed to approve custom fee request group");
        } finally {
          setApprovalLoading(false);
        }
      },
    });
  };

  /**
   * Approve individual student
   */
  const handleApproveStudent = async (customFeeRequestId, studentSpc) => {
    try {
      setApprovalLoading(true);
      const result = await approveCustomFee(customFeeRequestId);
      if (result === true) {
        const group = groupedRequests.find((g) =>
          g.students.some((s) => s.customFeeRequestId === customFeeRequestId),
        );
        const student = group?.students.find(
          (s) => s.studentSpc === studentSpc,
        );
        message.success(
          `Approved fee for ${student?.studentName || "student"}`,
        );
        await fetchCustomFees();
      } else {
        message.error("Failed to approve student fee");
      }
    } catch (error) {
      console.error("Approve student error:", error);
      message.error("Failed to approve student fee");
    } finally {
      setApprovalLoading(false);
    }
  };

  /**
   * Reject individual student
   */
  const handleRejectStudent = (customFeeRequestId, studentSpc) => {
    setRejectingStudentSpc(studentSpc);
    setRejectingStudentRequestId(customFeeRequestId);
    setRejectionReason("");
    setRejectModalVisible(true);
  };

  /**
   * Reject entire group
   */
  const handleRejectClick = (groupKey) => {
    setRejectingGroupKey(groupKey);
    setRejectingStudentSpc(null);
    setRejectionReason("");
    setRejectModalVisible(true);
  };

  /**
   * Confirm rejection
   */
  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      message.error("Please enter a rejection reason");
      return;
    }

    try {
      setApprovalLoading(true);

      if (rejectingStudentSpc && rejectingStudentRequestId) {
        // Single student rejection
        const result = await rejectCustomFee(rejectingStudentRequestId, {
          rejectionReason: rejectionReason,
        });
        if (result === true) {
          message.success(`Rejected fee for ${rejectingStudentSpc}`);
          await fetchCustomFees();
        } else {
          message.error("Failed to reject student fee");
        }
      } else if (rejectingGroupKey) {
        // Entire group rejection - sequential
        const group = groupedRequests.find(
          (g) => g.groupKey === rejectingGroupKey,
        );

        for (const requestId of group.customFeeRequestIds) {
          const result = await rejectCustomFee(requestId, {
            rejectionReason: rejectionReason,
          });
          if (result !== true) {
            throw new Error(`Failed to reject request ${requestId}`);
          }
        }
        message.success(`Rejected for ${group.students.length} student(s)`);
        setDetailDrawerVisible(false);
        await fetchCustomFees();
      }

      setRejectModalVisible(false);
      setRejectionReason("");
      setRejectingGroupKey(null);
      setRejectingStudentSpc(null);
      setRejectingStudentRequestId(null);
    } catch (error) {
      console.error("Reject error:", error);
      message.error("Failed to reject custom fee request");
    } finally {
      setApprovalLoading(false);
    }
  };

  /**
   * Remove student from group
   */
  const handleRemoveStudent = async (customFeeRequestId, studentSpc) => {
    Modal.confirm({
      title: "Remove Student from Group?",
      content:
        "This will delete this student's custom fee request from the group.",
      okText: "Remove",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setApprovalLoading(true);
          const result = await deleteCustomFee(customFeeRequestId);
          // Deletion is successful even if it returns error, just fetch again
          message.success(`Removed student from group`);
          await fetchCustomFees();
        } catch (error) {
          console.error("Remove student error:", error);
          message.error("Failed to remove student from group");
        } finally {
          setApprovalLoading(false);
        }
      },
    });
  };

  /**
   * Add students to group
   */
  const handleAddStudents = async (group, newStudentSpcs) => {
    try {
      setApprovalLoading(true);

      // Get feeTypeId from feeTypeCode lookup
      let feeTypeId = group?.feeTypeId;

      if (!feeTypeId && group?.feeTypeCode) {
        // Look up feeTypeId from feeTypes array using code
        const feeType = feeTypes.find((ft) => ft.code === group.feeTypeCode);
        feeTypeId = feeType?.feeTypeId || feeType?.id;
      }

      if (!feeTypeId) {
        message.error(
          "Could not determine fee type. Please refresh and try again.",
        );
        return;
      }

      let successCount = 0;
      for (const studentSpc of newStudentSpcs) {
        const data = {
          studentSpcs: [studentSpc],
          feeTypeId: parseInt(feeTypeId),
          description: group.description,
          amount: parseFloat(group.amount),
          acadyr: group.acadyr,
          notes: "",
        };

        const result = await bulkCreateCustomFees(data);
        if (result) {
          successCount++;
        }
      }

      if (successCount === newStudentSpcs.length) {
        message.success(`Added ${newStudentSpcs.length} student(s) to group`);
        setDetailDrawerVisible(false);
        await fetchCustomFees();
      } else {
        message.warning(
          `Added ${successCount} of ${newStudentSpcs.length} student(s)`,
        );
        await fetchCustomFees();
      }
    } catch (error) {
      console.error("Add students error:", error);
      message.error("Failed to add students to group");
    } finally {
      setApprovalLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Custom Fees | Finance</title>
      </Helmet>

      <div className="content-wrapper">
        <div className="mg-b-20">
          <h4 className="mg-b-5">Custom Fee Requests</h4>
          <p className="tx-color-03 mg-b-0">
            Manage custom fee requests for groups of students
          </p>
        </div>

        <Card
          title="Custom Fee Requests"
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Create Custom Fee
            </Button>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Input
                  placeholder="Search by description or fee type..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) =>
                    setFilters({ ...filters, searchText: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                >
                  <Option value="Pending">Pending</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Rejected">Rejected</Option>
                </Select>
              </Col>
            </Row>

            {(filters.searchText || filters.status) && (
              <div>
                <Tag color="blue">
                  Showing {filteredRequests.length} of {groupedRequests.length}{" "}
                  request group(s)
                </Tag>
              </div>
            )}

            <CustomFeeRequestsTable
              data={filteredRequests}
              loading={loading || approvalLoading}
              onView={handleViewDetails}
              onEdit={handleEdit}
              onApprove={handleApprove}
              onReject={handleRejectClick}
            />
          </Space>
        </Card>
      </div>

      <CustomFeeModal
        visible={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={handleCreateSuccess}
        bulkCreateCustomFees={bulkCreateCustomFees}
      />

      <EditCustomFeeModal
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedGroup(null);
        }}
        onSuccess={handleEditSuccess}
        groupData={selectedGroup}
        updateCustomFee={updateCustomFee}
      />

      <CustomFeeDetailDrawer
        visible={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
        groupData={selectedGroup}
        students={selectedGroup?.students || []}
        onApprove={handleApprove}
        onReject={handleRejectClick}
        onApproveStudent={handleApproveStudent}
        onRejectStudent={handleRejectStudent}
        onRemoveStudent={handleRemoveStudent}
        onAddStudents={handleAddStudents}
        loading={approvalLoading}
        groupKey={selectedGroup?.groupKey}
        allStudentsPending={selectedGroup?.students?.every(
          (s) => s.status === "Pending",
        )}
      />

      <Modal
        title={
          rejectingStudentSpc
            ? "Reject Student Fee"
            : "Reject Custom Fee Request Group"
        }
        open={rejectModalVisible}
        onOk={handleRejectConfirm}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectionReason("");
          setRejectingGroupKey(null);
          setRejectingStudentSpc(null);
          setRejectingStudentRequestId(null);
        }}
        okText="Reject"
        okType="danger"
        confirmLoading={approvalLoading}
      >
        <p>Please provide a reason:</p>
        <textarea
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          placeholder="Enter rejection reason..."
          rows={4}
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #d9d9d9",
            fontFamily: "inherit",
          }}
        />
      </Modal>
    </>
  );
};

export default CustomFeesManagement;
